/**
 * PanelPlanning drawing mixin — 그리기 도구 + 나누기 설정
 *
 * Thin wrapper: Vue/Ol 바인딩만 유지하고, 순수 로직은 panelDrawingService 에 위임.
 */
import { createPanelDrawingService } from '../services/panelDrawingService.js';
import {
  buildHierarchySelectionDisplay,
  findArrayTreeItemBySeq,
  findDefaultSelectionTreeItem,
  findTreeItemById
} from '../utils/mapHierarchySelectionContext.js';
import { resolveStringPlacementArraySeq } from '../utils/resolveStringPlacementArraySeq.js';
import { logStringCopySave } from '../utils/stringCopySaveDebug.js';
import {
  dispatchPanelPlanningAlert,
  getStringDrawTargetRequiredAlertMessage
} from '../utils/panelPlanningUserMessage.js';

export const drawSetup = {
  data() {
    return {
      /** Ctrl+드래그·붙여넣기 중 놓을 Array seq (미리보기) */
      stringPlacementPreviewArraySeq: null,
      /** 직접 그리기 시작 시 고정 — 확인 시점 focusedNodeId가 풀려도 Array·String 번호 유지 */
      lockedStringDrawTargetArraySeq: null,
      /** 그리기 완료 클릭이 빈 맵 선택 해제로 처리되지 않게 */
      _suppressMapSelectionUntil: 0,
      divisionPopupOpen: false
    };
  },

  computed: {
    _drawingService() {
      return createPanelDrawingService();
    },

    /** String 그리기 대상 Array seq (트리 선택=포커스) */
    stringDrawTargetArraySeq() {
      void this.focusedNodeId;
      void this.treeData;
      return this._drawingService.resolveCurrentArraySeq(this.treeData, this.focusedNodeId);
    },

    stringDrawTargetArrayNode() {
      void this.focusedNodeId;
      void this.treeData;
      return this._drawingService.resolveCurrentArrayNode(this.treeData, this.focusedNodeId);
    },

    hasStringDrawTargetArray() {
      return this.stringDrawTargetArraySeq != null;
    },

    stringDrawTargetArrayLabel() {
      const node = this.stringDrawTargetArrayNode;
      if (!node) return '';
      return (
        node.name ||
        `Array ${node.arrayId ?? node.solarArrayId ?? this.stringDrawTargetArraySeq}`
      );
    },

    /** sticky 바 — 현재 Array / String / Module */
    activeHierarchyTreeItem() {
      void this.selectionUiVersion;
      void this.focusedNodeId;
      void this.lastHighlightedItemId;
      void this.treeData;

      if (this.canDeleteSelectedModuleFromMap && this.moduleSelection?.getLength?.()) {
        const treeItem = this.findTreeItemByMapFeature?.(this.moduleSelection.getArray()[0]);
        if (treeItem) return treeItem;
      }

      const id = this.lastHighlightedItemId ?? this.focusedNodeId;
      if (!id) return null;
      return findTreeItemById(this.treeData, id);
    },

    hierarchySelectionDisplay() {
      return buildHierarchySelectionDisplay({
        treeData: this.treeData,
        activeTreeItem: this.activeHierarchyTreeItem,
        stringDrawTargetArrayLabel: this.stringDrawTargetArrayLabel,
        hasStringDrawTargetArray: this.hasStringDrawTargetArray
      });
    },

    stringPlacementPreviewLabel() {
      void this.stringPlacementPreviewArraySeq;
      void this.treeData;
      if (this.stringPlacementPreviewArraySeq == null) return '';
      return this.labelForArraySeq(this.stringPlacementPreviewArraySeq);
    },

    isStringPlacementPreviewActive() {
      return (
        (this._mapCtrlDragActive || this.isPasteMode) &&
        this.stringPlacementPreviewArraySeq != null &&
        this.stringPlacementPreviewLabel
      );
    }
  },

  methods: {
    /** 트리·지도 선택 → String 그리기 대상 Array (선택=포커스) */
    syncStringDrawTargetFromTreeItem(item) {
      if (!item?.id || !['array', 'string', 'module'].includes(item.type)) {
        return;
      }
      this.focusedNodeId = item.id;
    },

    clearStringDrawTarget() {
      this.focusedNodeId = null;
      this.clearLockedStringDrawTarget();
    },

    lockStringDrawTargetForDrawing() {
      const seq = this.stringDrawTargetArraySeq;
      if (seq != null) {
        this.lockedStringDrawTargetArraySeq = seq;
      }
    },

    suppressMapSelectionAfterDraw(ms = 400) {
      this._suppressMapSelectionUntil = Date.now() + ms;
    },

    clearLockedStringDrawTarget() {
      this.lockedStringDrawTargetArraySeq = null;
    },

    resolveStringCreateTargetArraySeq(options = {}) {
      return (
        options?.solarArraySeq ??
        this.lockedStringDrawTargetArraySeq ??
        this.stringDrawTargetArraySeq ??
        this._resolveCurrentArraySeq()
      );
    },

    /** Array 미지정 시 — 액션 가드 실패 alert (sticky는 짧은 상태만) */
    warnStringDrawTargetArrayRequired() {
      dispatchPanelPlanningAlert(this.$store, {
        type: 'warning',
        message: getStringDrawTargetRequiredAlertMessage(
          key => this.$t(key),
          this.treeData
        )
      });
    },

    ensureStringDrawTargetArrayForDrawing() {
      if (this.hasStringDrawTargetArray) {
        return true;
      }
      this.warnStringDrawTargetArrayRequired();
      return false;
    },

    /** String / 다중 String 붙여넣기·복사 배치 전 Array 선택 확인 */
    ensureStringDrawTargetArrayForCopyPaste({ dropCoordinate, sourceArraySeq } = {}) {
      const resolved = this.resolveStringPlacementArraySeqForDrop({
        dropCoordinate,
        sourceArraySeq
      });
      if (resolved != null) {
        this.syncStringDrawTargetFromArraySeq(resolved);
        return true;
      }
      this.warnStringDrawTargetArrayRequired();
      return false;
    },

    /**
     * 놓는 위치·원본·트리 포커스 순으로 String이 들어갈 Array seq
     */
    resolveStringPlacementArraySeqForDrop({ dropCoordinate, sourceFeature, sourceArraySeq } = {}) {
      const fromFeature =
        sourceArraySeq ??
        (sourceFeature?.get?.('solarArraySeq') ?? sourceFeature?.get?.('solarArrayId') ?? null);

      return resolveStringPlacementArraySeq({
        map: this.map,
        vectorLayer: this.vectorLayer,
        vectorSource: this.vectorSource,
        dropCoordinate,
        sourceArraySeq: fromFeature,
        treeArraySeq: this.stringDrawTargetArraySeq
      });
    },

    syncStringDrawTargetFromMapFeature(feature) {
      const treeItem = this.findTreeItemByMapFeature?.(feature);
      if (!treeItem) return;
      this.syncStringDrawTargetFromTreeItem(treeItem);
      this.lastHighlightedItemId = treeItem.id;
    },

    syncStringDrawTargetFromArraySeq(arraySeq) {
      const item = findArrayTreeItemBySeq(this.treeData, arraySeq);
      if (!item) return;
      this.syncStringDrawTargetFromTreeItem(item);
      this.lastHighlightedItemId = item.id;
    },

    labelForArraySeq(arraySeq) {
      const item = findArrayTreeItemBySeq(this.treeData, arraySeq);
      if (!item) {
        return arraySeq != null ? `Array ${arraySeq}` : '';
      }
      return item.name || `Array ${arraySeq}`;
    },

    updateStringPlacementPreview({ dropCoordinate, sourceFeature, sourceArraySeq } = {}) {
      this.stringPlacementPreviewArraySeq = this.resolveStringPlacementArraySeqForDrop({
        dropCoordinate,
        sourceFeature,
        sourceArraySeq
      });
    },

    clearStringPlacementPreview() {
      this.stringPlacementPreviewArraySeq = null;
    },

    /** 사이트 로드 후: 마지막 Array의 마지막 String(없으면 Array) 선택 */
    applyInitialHierarchySelection() {
      const item = findDefaultSelectionTreeItem(this.treeData);
      if (!item) {
        this.clearStringDrawTarget();
        this.lastHighlightedItemId = null;
        return;
      }
      this.syncStringDrawTargetFromTreeItem(item);
      this.lastHighlightedItemId = item.id;
      if (typeof this.applyTreeItemMapSelection === 'function') {
        this.applyTreeItemMapSelection(item);
      }
      this.$nextTick(() => {
        this.$refs.moduleVirtualTree?.scrollToNodeId?.(item.id);
      });
    },
    /* ───────── drawing toolbar (Ol) ───────── */

    startDrawingMode(mode) {
      if (!this.drawingToolbar) return;
      this.drawingToolbar.activeTool = mode;
    },

    exitDrawingMode() {
      if (!this.drawingToolbar) return;
      this.drawingToolbar.activeTool = null;
      this.clearLockedStringDrawTarget();
      if (this.drawnPolygon) {
        this.vectorSource.removeFeature(this.drawnPolygon);
        this.drawnPolygon = null;
      }
    },

    /* ───────── popup (Vue + Ol) ───────── */

    showDivisionPopup(drawnPolygon) {
      if (drawnPolygon) {
        this.drawnPolygon = drawnPolygon;
      }

      if (this.autoDrawEnabled) {
        void this.confirmDivision();
        return;
      }

      if (!this.drawnPolygon?.getGeometry?.()) {
        console.warn('showDivisionPopup: drawnPolygon 없음');
        return;
      }

      if (!this.popup) {
        this.initializeMapClickPopup?.();
      }
      if (!this.popup) {
        console.warn('showDivisionPopup: popup 초기화 실패');
        return;
      }

      this.divisionPopupOpen = true;

      const extent = this.drawnPolygon.getGeometry().getExtent();
      const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

      const showAtCenter = () => {
        this.popup.setPosition(center);
        const el = this.popup.getElement?.();
        if (el) {
          el.style.display = 'block';
          el.style.visibility = 'visible';
        }
      };

      if (typeof this.$nextTick === 'function') {
        this.$nextTick(showAtCenter);
      } else {
        showAtCenter();
      }
    },

    /* ───────── user-facing actions (Vue) ───────── */

    async confirmDivision(options = {}) {
      const targetArraySeq = this.resolveStringCreateTargetArraySeq(options);
      logStringCopySave('confirmDivision → enter', {
        moduleColNum: this.moduleColNum,
        moduleRowNum: this.moduleRowNum,
        solarArraySeq: targetArraySeq,
        lockedArraySeq: this.lockedStringDrawTargetArraySeq,
        ctrlDragCopy: Boolean(options?._ctrlDragCopySourceFeature),
        hasDrawnPolygon: Boolean(this.drawnPolygon)
      });
      if (this.moduleColNum < 1 || this.moduleRowNum < 1) {
        alert(this.$t('panelPlanning.alertMinRowCol'));
        return;
      }
      if (targetArraySeq == null) {
        this.warnStringDrawTargetArrayRequired();
        return;
      }
      this.divisionPopupOpen = false;
      this.popup?.setPosition(undefined);
      await this.dividePolygon({ ...options, solarArraySeq: targetArraySeq });
      // 연속 그리기 — 다음 String도 같은 Array·번호 체인 유지
      this.lockedStringDrawTargetArraySeq = targetArraySeq;
      logStringCopySave('confirmDivision → exit');
    },

    cancelDivision() {
      this.divisionPopupOpen = false;
      this.popup?.setPosition(undefined);
      this.clearLockedStringDrawTarget();
      if (this.drawnPolygon) {
        this.vectorSource.removeFeature(this.drawnPolygon);
        this.drawnPolygon = null;
      }
    },

    /* ───────── delegated to service ───────── */

    async dividePolygon(options = {}) {
      return this.runWithSiteDesignSaving(() =>
        this._drawingService.dividePolygon(this._drawingContext(options), options)
      );
    },

    async refreshSiteDesignAfterStringChanges() {
      return this.syncLocalStateAfterStringChanges();
    },

    async syncLocalStateAfterStringChanges() {
      await this._drawingService.syncLocalStateAfterStringChanges(this._drawingContext());
    },

    interpolate(p1, p2, t) {
      return this._drawingService.interpolate(p1, p2, t);
    },

    _resolveCurrentArraySeq() {
      return this._drawingService.resolveCurrentArraySeq(this.treeData, this.focusedNodeId);
    },

    /* ───────── internal helpers ───────── */

    /**
     * 서비스가 필요로 하는 모든 컨텍스트를 한 객체로 묶어 반환.
     * this 바인딩은 mixin 메서드가 책임지고, 서비스는 순수 객체만 본다.
     */
    _drawingContext(extra = {}) {
      const vm = this;
      return {
        // site
        get selectedSite() { return vm.selectedSite; },
        // OpenLayers
        get drawnPolygon() { return vm.drawnPolygon; },
        set drawnPolygon(v) { vm.drawnPolygon = v; },
        get vectorSource() { return vm.vectorSource; },
        // division params
        get moduleColNum() { return vm.moduleColNum; },
        get moduleRowNum() { return vm.moduleRowNum; },
        get moduleNumberingOrientation() { return vm.moduleNumberingOrientation; },
        // module dimensions
        get moduleWidth() { return vm.moduleWidth; },
        set moduleWidth(v) { vm.moduleWidth = v; },
        get moduleHeight() { return vm.moduleHeight; },
        set moduleHeight(v) { vm.moduleHeight = v; },
        // data
        get solarStrings() { return vm.solarStrings; },
        set solarStrings(v) { vm.solarStrings = v; },
        get flattenedSiteDesign() { return vm.flattenedSiteDesign; },
        set flattenedSiteDesign(v) { vm.flattenedSiteDesign = v; },
        get treeData() { return vm.treeData; },
        get focusedNodeId() { return vm.focusedNodeId; },
        get lockedStringDrawTargetArraySeq() { return vm.lockedStringDrawTargetArraySeq; },
        get stringDrawTargetArraySeq() { return vm.stringDrawTargetArraySeq; },
        // saving flag
        get isSavingSiteDesign() { return vm.isSavingSiteDesign; },
        set isSavingSiteDesign(v) { vm.isSavingSiteDesign = v; },
        // ctrl+drag copy debug
        get _ctrlDragCopySourceFeature() { return extra._ctrlDragCopySourceFeature ?? null; },
        get _ctrlDragCopyDebug() { return Boolean(extra._ctrlDragCopySourceFeature); },
        get skipGridSlots() { return extra.skipGridSlots ?? null; },
        // forwarded methods (service calls these back on the Vue component)
        get convToWGS84() { return typeof vm.convToWGS84 === 'function' ? vm.convToWGS84.bind(vm) : undefined; },
        get drawSiteDesignOnMap() { return typeof vm.drawSiteDesignOnMap === 'function' ? vm.drawSiteDesignOnMap.bind(vm) : undefined; },
        get syncPanelPlanningLocalStateFromFlattened() {
          return typeof vm.syncPanelPlanningLocalStateFromFlattened === 'function'
            ? vm.syncPanelPlanningLocalStateFromFlattened.bind(vm)
            : undefined;
        },
        get syncFlattenedToStore() { return typeof vm.syncFlattenedToStore === 'function' ? vm.syncFlattenedToStore.bind(vm) : undefined; },
        get applyStringCreatedPatch() { return typeof vm.applyStringCreatedPatch === 'function' ? vm.applyStringCreatedPatch.bind(vm) : undefined; }
      };
    }
  }
};
